import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { Session } from '@supabase/supabase-js'
import { useAuth } from '../../lib/auth.context'
import { env } from '../../lib/env'
import { getUserProfile } from '../../lib/api/api'
import { InputOTP } from '../input-otp'

interface TOTPVerifyProps {
  onComplete?: () => void
  redirectTo?: string
}

export function TOTPVerify({ onComplete, redirectTo }: TOTPVerifyProps) {
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const navigate = useNavigate()
  const { toast } = useToast()
  const { setIsTOTPVerified } = useAuth()

  useEffect(() => {
    const getTOTPFactor = async () => {
      try {
        // Wait longer to ensure session is properly established
        await new Promise(resolve => setTimeout(resolve, 500))

        // Try multiple times to get the session if it's not immediately available
        let currentSession = null
        let attempts = 0
        const maxAttempts = 5

        while (!currentSession && attempts < maxAttempts) {
          const { data: { session } } = await supabase.auth.getSession()
          currentSession = session

          if (!currentSession) {
            attempts++
            if (attempts < maxAttempts) {
              console.log(`[TOTPVerify] No session found, attempt ${attempts}/${maxAttempts}, retrying in 200ms...`)
              await new Promise(resolve => setTimeout(resolve, 200))
            }
          }
        }

        if (!currentSession) {
          throw new Error('No active session found. Please log in again.')
        }

        console.log('[TOTPVerify] Session verified, getting TOTP factors')
        const { data: factors } = await supabase.auth.mfa.listFactors()
        if (!factors?.totp?.length) throw new Error('No TOTP factors found')

        // Find the first verified TOTP factor
        const verifiedTotpFactor = factors.totp.find(factor => factor.status === 'verified')
        if (!verifiedTotpFactor) throw new Error('No verified TOTP factor found')

        console.log('[TOTPVerify] verified totpFactor:', verifiedTotpFactor)
        setFactorId(verifiedTotpFactor.id)
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to get TOTP factor'
        console.error('[TOTPVerify] Error getting TOTP factor:', error)
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    getTOTPFactor()
  }, [toast])

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      if (!factorId) throw new Error('TOTP factor not found')

      // Verify we still have a valid session
      const { data: { session: existingSession } } = await supabase.auth.getSession()
      if (!existingSession) {
        throw new Error('Session expired. Please log in again.')
      }

      console.log('[TOTPVerify] factorId:', factorId, 'session user:', existingSession.user?.id)
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })

      if (error) throw error

      const { data, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode
      })

      if (verifyError) throw verifyError

      // Create a new session object from the response
      const newSession: Session = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
        expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
        token_type: data.token_type,
        user: data.user
      }

      // Set the new session with aal2 token
      await supabase.auth.setSession(newSession)

      // Update TOTP verification status in auth context
      console.log('[TOTPVerify] Setting TOTP verification to true after successful verification')
      setIsTOTPVerified(true)

      toast({
        title: 'Success',
        description: 'TOTP verification successful',
      })

      // Get user profile to check role
      const userProfile = await getUserProfile()
      const isPartnerUser = userProfile.userRole?.startsWith('entity_')

      if (onComplete) {
        onComplete()
      }

      // Determine redirect based on user role
      if (isPartnerUser && env.VITE_USER_DASHBOARD_URL) {
        // For external URLs, use window.location.href with full URL
        window.location.replace(env.VITE_USER_DASHBOARD_URL)
      } else if (redirectTo?.startsWith('http')) {
        // Handle any other external URLs
        window.location.replace(redirectTo)
      } else if (redirectTo) {
        // Internal route navigation
        navigate(redirectTo)
      } else {
        // Default internal route
        navigate('/admin/dashboard')
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
    setVerifyCode(value)
  }

  return (
    <div className="flex min-h-[500px]">
      {/* Left Panel - Marketing Content */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}/>
        {/* Header */}
        <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
          <div className="flex items-center">
            <img src="/fanfairreversed.png" alt="FanFair" className="h-8"/>
          </div>
        </div>
        {/* Content */}
        <div className="relative z-10 w-full flex items-center justify-center p-12">
          <div className="text-center text-white max-w-md">
            <h1 className="text-4xl font-bold mb-4 text-white">
              2FA Verification
            </h1>
            <p className="text-lg opacity-90 mb-8 text-white">
            </p>
            {/* Slide indicators */}
            <div className="flex justify-center space-x-2">
              <div className="w-8 h-1 bg-white rounded-full"></div>
              <div className="w-1 h-1 bg-white/40 rounded-full"></div>
              <div className="w-1 h-1 bg-white/40 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile Header */}
          <div className="lg:hidden text-center">
            <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-medium text-white">Enter the 6-digit code from your authentication app</h1>
            <p className="text-slate-400 font-light">
              (Google Authenticator, Microsoft Authenticator, etc).
            </p>
          </div>
          <div>
            <form onSubmit={handleVerify}>
              <div className="flex flex-col items-center gap-8 w-full">
                {formError && (
                  <Alert variant="destructive">
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                )}
                <InputOTP
                  length={6} onChange={(value) => setVerifyCode(value)} />
                <Button
                  type="submit"
                  disabled={isLoading || !verifyCode}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {isLoading ? 'Verifying...' : 'Verify Code'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>


    // NOTE: To Be Remove after Login Credential issue
    // <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
    //   <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>
    //     Two-Factor Authentication Required
    //   </h1>
    //   <p className={`mb-4 ${getThemeClass('components.text.body')}`}>
    //     Please enter the verification code from your authenticator app to continue.
    //   </p>
    //   <form onSubmit={handleVerify} className="space-y-4">
    //     <div>
    //       <Input
    //         type="text"
    //         inputMode="numeric"
    //         pattern="[0-9]*"
    //         maxLength={6}
    //         placeholder="Enter 6-digit code"
    //         value={verifyCode}
    //         onChange={handleCodeChange}
    //         required
    //         disabled={isLoading}
    //         className={getThemeClass('components.input.base')}
    //         autoFocus
    //       />
    //     </div>
    //     {formError && (
    //       <Alert variant="destructive" className="mt-4">
    //         <AlertDescription>{formError}</AlertDescription>
    //       </Alert>
    //     )}
    //     <Button
    //       type="submit"
    //       className={`w-full ${getThemeClass('components.button.primary')}`}
    //       disabled={isLoading || verifyCode.length !== 6}
    //     >
    //       {isLoading ? 'Verifying...' : 'Verify and Continue'}
    //     </Button>
    //   </form>
    // </Card>
  )
}
